import { AlertCircle, Check, CheckCheck, LoaderCircle } from 'lucide-react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@avanta/shared/components/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@avanta/shared/components/ui/Popover';
import { MessageStatus } from '@avanta/shared/constants/chat';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useIsMounted } from '@avanta/shared/hooks/useIsMounted';
import {
  useActiveOtherUserId,
  useActiveOtherUserName,
} from '@avanta/shared/stores/chat/conversations';
import { useMessagesStore } from '@avanta/shared/stores/chat/messages';
import type { MessageFile, OptimisticMessage } from '@avanta/shared/types/chat';
import { determineUserReadableDate } from '@avanta/shared/utils/chat';
import { cn } from '@avanta/shared/utils/cn';
import { isHtmlMessage, stripHtmlTags } from '@avanta/shared/utils/text';
import { RetryMessageButton } from '..';
import { RichMessageText } from './RichMessageText';
import { type ContentProps } from './types';
import { getFileComponent, isMessageReadByOther } from './utils';

const MessageStatusIcon = {
  sent: <Check className="size-4" />,
  read: <CheckCheck className="size-4" />,
  sending: <LoaderCircle className="size-4 animate-spin text-gray-400" />,
  failed: <AlertCircle className="size-4 text-red-500" />,
};

const TIMESTAMP_OVERLAP_CHAR_THRESHOLD = 16;

const ContentContent = ({ message, isEditing }: ContentProps): React.ReactElement => {
  const otherUserId = useActiveOtherUserId();
  const otherUserName = useActiveOtherUserName();
  const { upsertMessage } = useMessagesStore();
  const isDeleted = message.deleted;
  const isEdited = !!message.actions?.edited && !isDeleted;
  const isMine = message.userId !== otherUserId;
  const isSending = message.status === MessageStatus.sending;
  const isFailed = message.status === MessageStatus.failed;

  const isReadByOther = useMessagesStore(state =>
    isMessageReadByOther(state.readReceipts.get(message.channelId), otherUserId, message.timetoken)
  );
  const isRead = !isSending && !isFailed && isReadByOther;
  const showFiles = !isDeleted && message.files && message.files.length > 0;
  const reactions = message?.reactions;
  const isMounted = useIsMounted();
  const { t } = useTranslation(LOCIZE_NS.CHAT);

  const userName = (id: string): string | undefined =>
    otherUserId === id ? otherUserName : t('Chat.Reactions.you');

  const handleRemoveReaction = async (reaction: string): Promise<void> => {
    if (!isMounted()) return;
    const updated = await message.toggleReaction(reaction);
    (updated as unknown as Record<string, unknown>)['status'] = MessageStatus.sent;
    upsertMessage(message.channelId, updated as OptimisticMessage);
  };

  const getStatusIcon = (): React.ReactNode => {
    if (isFailed) return MessageStatusIcon.failed;
    if (isSending) return MessageStatusIcon.sending;
    if (isRead) return MessageStatusIcon.read;
    return MessageStatusIcon.sent;
  };

  const isHtml = isHtmlMessage(message.text);
  const textLength = isHtml ? stripHtmlTags(message.text).length : message.text.length;
  const needsBottomPadding = textLength > TIMESTAMP_OVERLAP_CHAR_THRESHOLD || isEdited || showFiles;
  const hasReactions = !isEditing && !!reactions && Object.keys(reactions).length > 0;

  const files = showFiles
    ? message.files.map((file: MessageFile['file']) => {
        const Component = getFileComponent(file.type);
        if (!Component) return null;
        return <Component key={file.name} file={file} />;
      })
    : null;

  return (
    <p className="flex relative flex-col gap-2">
      {files}
      {!isEditing && (
        <span className={cn('flex relative items-end gap2', isEdited && 'gap-8')}>
          <span
            className={cn(
              'break-word',
              !isHtml && 'whitespace-pre-line',
              isDeleted && 'italic text-gray-500 opacity-50 text-sm',
              needsBottomPadding && 'pb-6'
            )}
          >
            {isHtml ? <RichMessageText html={message.text} /> : message.text}
          </span>
          <span className="flex items-center gap-2 absolute bottom-0 right-0">
            {isEdited && (
              <span className="text-sm text-gray-500 text-left whitespace-nowrap">
                {t('Chat.Messages.edited')}
              </span>
            )}
            <span className="text-xs text-gray-500 text-left whitespace-nowrap">
              {isSending
                ? t('Chat.Messages.sending')
                : determineUserReadableDate(message.timetoken)}
            </span>
            {isMine && <span className="text-gray-500 text-left">{getStatusIcon()}</span>}
          </span>
        </span>
      )}
      {isFailed && isMine && <RetryMessageButton message={message} />}
      {hasReactions && (
        <Popover>
          <PopoverTrigger className="flex items-center gap-2 absolute bottom-[-32px] left-0 bg-white py-1 px-2 rounded-md">
            {Object.keys(reactions).map(reaction => (
              <span key={reaction}>{reaction}</span>
            ))}
          </PopoverTrigger>
          <PopoverContent className="w-full ml-1 p-2.5">
            {Object.entries(reactions).flatMap(([reaction, reactionData]) =>
              reactionData.map(data => {
                const isMyReaction = data.uuid !== otherUserId;
                return (
                  <Button
                    variant="ghost"
                    className="flex items-center justify-between gap-6 w-full py-6 disabled:opacity-100"
                    key={data.actionTimetoken}
                    onClick={() => handleRemoveReaction(reaction)}
                    disabled={!isMyReaction}
                  >
                    <div className="flex flex-col items-start w-full">
                      <p className="text-sm">{userName(data.uuid)}</p>
                      {isMyReaction && (
                        <p className="text-[10px] text-gray-500">{t('Chat.Reactions.remove')}</p>
                      )}
                    </div>
                    <span className="text-xl">{reaction}</span>
                  </Button>
                );
              })
            )}
          </PopoverContent>
        </Popover>
      )}
    </p>
  );
};

export const Content = memo(ContentContent);
